import React, {useState} from 'react'
import {instance as journalService} from '../../../services/JournalService'
import {instance as plantsService} from '../../../services/PlantsService'
import {
  Button,
  Divider,
  List,
  Popconfirm,
  Spin,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd'
const {CheckableTag} = Tag
import {JournalEventForm} from '../../journalPage/JournalEventForm'
import {useAsyncValue} from '../../../hooks/useAsyncValue'
import {useCurrentUser} from '../../../hooks/useCurrentUser'
import iconPortal from 'url:../../../../public/assets/images/iconPortal2.svg'
import '../plantForm.less'
import {useIntl} from 'react-intl'
import {ImagePreview} from './ImagePreview'
import {Panel} from './Panel'
import {ToDoForm} from '../../todos/ToDoForm'
import {
  ToDoTypeMetadata,
  getToDoTypeInfo,
  getJournalEventTypeInfo,
  JournalRecord,
  EventTypeMetadata,
  CustomJournalEventType,
  ToDoType,
  JournalEventType,
  CustomToDoType,
  PredefinedJournalEventType,
} from '../../../entities/journalRecord'
import {CheckOutlined, PlusOutlined, QuestionCircleOutlined} from '@ant-design/icons'
import {Link, useRevalidator, useSubmit} from 'react-router-dom'
import {ToDo} from '../../../entities/todoList'
import {FinishToDoButton} from '../../todos/FinishToDoButton'
import dayjs from 'dayjs'
import {markPlantForRevalidation} from '../../../routes/plantRevalidator'
import {markToDoForRevalidation} from '../../../routes/todoRevalidator'
import {EmEmoji} from '../../emoji/EmEmoji'

const filters = Object.fromEntries([
  ...Object.entries(EventTypeMetadata).map(([key, meta]) => [key, false]),
  ...Object.entries(ToDoTypeMetadata).map(([key, meta]) => [key, false]),
])

export type PlantTimeEventsProps = {
  currentPlantId: number
  currentPlantName?: string
  timeEvents: JournalRecord[]
  plantToDos: ToDo[]
  userEventTypes: CustomJournalEventType[]
  userToDoTypes: CustomToDoType[]
  isPlantDeleted?: boolean
}

export function PlantTimeEvents({
  currentPlantId,
  currentPlantName,
  timeEvents: plantTimeEvents,
  plantToDos,
  userToDoTypes,
  userEventTypes,
  isPlantDeleted,
}: PlantTimeEventsProps) {
  const {
    token: {colorPrimary, colorPrimaryBorder, borderRadiusXS},
  } = theme.useToken()

  const [viewedTypesOfEvents, setViewedTypesOfEvents] = useState<Record<string, boolean>>(filters)
  const [isAddingEvent, setAddingEvent] = useState(false)
  const [isAddingTask, setAddingTask] = useState(false)
  const [currentPlantEventId, setCurrentPlantEventId] = useState<number | null>(null)
  const [currentPlantTask, setCurrentPlantTask] = useState<ToDo | null>(null)
  const user = useCurrentUser()
  const intl = useIntl()
  const revalidator = useRevalidator()

  const submit = useSubmit()
  const refreshPlantData = () => submit(null, {method: 'post'})
  const [messageApi, contextHolder] = message.useMessage()

  //const [toDoTypes, setToDoTypes] = useState<CustomToDoType[]>(userToDoTypes)

  const {
    value: plantedPlantsNamesWithId,
    isLoading: plantedPlantsNamesWithIdLoading,
    error,
  } = useAsyncValue(
    [],
    async () => {
      const plantedPlantsNamesWithId = await plantsService.getUserPlantsNamesWithId()
      return plantedPlantsNamesWithId
    },
    [currentPlantId]
  )

  const noFiltersSelected = Object.values(viewedTypesOfEvents).every((type) => type === false)

  return (
    <div className="plant-time">
      <div className="plant-time__title title--feature">
        {intl.formatMessage({id: 'myPlantTimeline'})}
      </div>
      <Panel title={intl.formatMessage({id: 'taskAndEventFilters'})}>
        <div className="event__filters" id="timeline-filters">
          {/* <Divider type="horizontal" orientation="left" className="event__filters__divider">
          {intl.formatMessage({id: 'filters'})}
        </Divider> */}
          <div>
            {Object.entries(viewedTypesOfEvents).map(([key, isChecked]) => (
              <CheckableTag
                className="tag"
                key={key}
                checked={isChecked}
                onChange={(checked) =>
                  setViewedTypesOfEvents((current) => ({...current, [key]: checked}))
                }
              >
                {intl.formatMessage({id: `eventType-${key}`})}
              </CheckableTag>
            ))}
          </div>

          <Button
            id="reset-filters"
            onClick={async () => {
              setViewedTypesOfEvents(filters)
            }}
            type="dashed"
          >
            {intl.formatMessage({id: 'resetFilters'})}
          </Button>
          {/* <Divider className="event__filters__divider" type="horizontal" orientation="left"></Divider> */}
        </div>
      </Panel>

      <div className="plant-time-todos">
        <div className="title">{intl.formatMessage({id: 'plannedTasks'})}</div>
        {plantToDos.length > 0 && (
          <div className="plant-time-todos__list">
            <List
              //style={{backgroundColor: '#c7e0dc', margin: '10px 0'}}
              bordered
              dataSource={plantToDos.filter(
                (todo) =>
                  (todo.type ? viewedTypesOfEvents[todo.type.type] : false) || noFiltersSelected
              )}
              renderItem={(toDoItem) => (
                <List.Item className="todo-item">
                  {currentPlantTask?.id !== toDoItem.id ? (
                    <Link
                      className="todo-item__title"
                      to="#"
                      onClick={async (ev) => {
                        ev.preventDefault()
                        setCurrentPlantTask(toDoItem)
                      }}
                    >
                      <div
                        className="todo-item__title__type"
                        style={{display: 'flex', justifyContent: 'space-between'}}
                      >
                        <EmEmoji id={getToDoTypeInfo(toDoItem.type).icon} size="1em"></EmEmoji>{' '}
                        <span style={{marginLeft: '5px'}}>
                          {toDoItem.type?.type === 'custom' || !toDoItem.type?.type
                            ? getToDoTypeInfo(toDoItem.type).name
                            : intl.formatMessage({id: `eventType-${toDoItem.type?.type}`})}
                        </span>
                      </div>
                      <span style={{color: 'grey'}}>
                        {toDoItem.targetEndDateUtc
                          ? `${dayjs(toDoItem.targetStartDateUtc).format('ll')} - ${dayjs(
                              toDoItem.targetEndDateUtc
                            ).format('ll')}`
                          : toDoItem.targetStartDateUtc
                          ? dayjs(toDoItem.targetStartDateUtc).format('ll')
                          : ''}
                      </span>
                      <Tooltip title={intl.formatMessage({id: `finishTask`})} color={colorPrimary}>
                        <div onClick={(e) => e.stopPropagation()}>
                          <FinishToDoButton
                            userJournalEventTypes={userEventTypes}
                            finishedTodo={toDoItem}
                            isText={false}
                          />
                        </div>
                      </Tooltip>
                    </Link>
                  ) : (
                    <div className="todo-item__form">
                      <ToDoForm
                        currentToDo={toDoItem}
                        currentPlant={{id: currentPlantId, name: currentPlantName ?? ''}}
                        toDoTypes={userToDoTypes}
                        //setToDoTypes={setToDoTypes}
                        userEventTypes={userEventTypes}
                        closeModal={() => {
                          setCurrentPlantTask(null)
                          setAddingTask(false)
                        }}
                        plantedPlantsNamesWithId={plantedPlantsNamesWithId}
                        userPlantCollections={[]}
                        userPlantCollectionsWithPlantsIds={[]}
                      />
                    </div>
                  )}
                </List.Item>
              )}
            />
          </div>
        )}

        {isAddingTask && (
          <div className="todo-item__new">
            <ToDoForm
              currentPlant={{id: currentPlantId, name: currentPlantName ?? ''}}
              toDoTypes={userToDoTypes}
              //setToDoTypes={setToDoTypes}
              userEventTypes={userEventTypes}
              closeModal={() => {
                setAddingTask(false)
              }}
              plantedPlantsNamesWithId={plantedPlantsNamesWithId}
              userPlantCollections={[]}
              userPlantCollectionsWithPlantsIds={[]}
            />
            {/* <ToDoForm
            onModalClose={(shouldRefresh) => {
              setAddingTask(false)
              if (shouldRefresh) {
                refreshPlantData()
              }
            }}
            currentPlant={{id: currentPlantId, name: currentPlantName}}
            isModalOpen={isAddingTask}
            listId={0}
          ></ToDoForm> */}
          </div>
        )}

        {!(isAddingEvent || isAddingTask) && !isPlantDeleted && (
          <div>
            <Button
              onClick={async () => {
                setAddingTask(true)
              }}
              //type="primary"
              className="add__new__event__button"
            >
              <PlusOutlined />
              {intl.formatMessage({id: 'addNewTask'})}
            </Button>
          </div>
        )}
      </div>

      <Divider />

      <div className="plant-time-events">
        <div className="title">{intl.formatMessage({id: 'pastEvents'})}</div>
        <Timeline
          className="timeline"
          items={plantTimeEvents
            .sort((a, b) => (a.actualCompletionDateUtc < b.actualCompletionDateUtc ? 1 : -1))
            .filter(
              (event) =>
                (event.type ? viewedTypesOfEvents[event.type.type] : false) || noFiltersSelected
            )
            .map((e) => ({
              // An item that has its edit form open is no longer a point on the
              // timeline, so it gives up the date column and the dot it would
              // otherwise be indented behind - see `.timeline__item--editing`.
              className: currentPlantEventId === e.id ? 'timeline__item--editing' : undefined,
              content: (
                <div>
                  {/* {e.photos && <ImagePreview photosIds={e.photos} />} */}
                  {currentPlantEventId === e.id ? (
                    <div className="todo-item__form">
                      <JournalEventForm
                        onFormClose={() => setCurrentPlantEventId(null)}
                        currentEvent={e}
                        onPlantEventListUpdated={refreshPlantData}
                        plantId={currentPlantId}
                        userJournalEventTypes={userEventTypes}
                      ></JournalEventForm>
                    </div>
                  ) : (
                    <div>
                      <Link
                        to="#"
                        onClick={async (ev) => {
                          ev.preventDefault()
                          setCurrentPlantEventId(e.id)
                        }}
                      >
                        {(e.type?.type === 'custom' && e.type.customJournalEventType) ||
                        !e.type?.type
                          ? getJournalEventTypeInfo(e.type).name
                          : intl.formatMessage({
                              id: `eventType-${e.type?.type}`,
                            })}
                      </Link>
                      <div>{e.comment}</div>
                    </div>
                  )}
                </div>
              ),
              title:
                currentPlantEventId === e.id
                  ? ''
                  : e.actualCompletionDateUtc
                  ? e.actualCompletionDateUtc.toString()
                  : '',
              color: getJournalEventTypeInfo(e.type).color,
              icon:
                currentPlantEventId === e.id ? (
                  <></>
                ) : (
                  <img src={iconPortal} className="timeline__icon"></img>
                ),
            }))}
          mode={'start'}
          titleSpan="130px"
        />
        {!(isAddingEvent || isAddingTask) && !isPlantDeleted && (
          <div>
            <Button
              onClick={async () => {
                setAddingEvent(true)
              }}
              //type="primary"
              className="add__new__event__button"
            >
              <PlusOutlined />
              {intl.formatMessage({id: 'addNewEvent'})}
            </Button>
          </div>
        )}

        {isAddingEvent && (
          <div className="todo-item__new">
            <JournalEventForm
              onFormClose={() => setAddingEvent(false)}
              onPlantEventListUpdated={refreshPlantData}
              plantId={currentPlantId}
              userJournalEventTypes={userEventTypes}
            ></JournalEventForm>
          </div>
        )}
      </div>
    </div>
  )
}
